<!--
/**
 * Port of apps/admin/src/components/Contract.vue (Phase 2.3).
 *
 * Global, self-contained dialog mounted by App.vue for sr/me/ba venues
 * (contract-billed tenants; non-sr/me/ba venues get Subscription.vue instead)
 * and for every inactive tenant.
 *
 * It is also the admin's account lock: while the stored tenant is inactive
 * (TenantService.isInactive — aria-api refuses it with a 402, which records the
 * status), the dialog is open whatever the billing settings, cannot be closed,
 * and offers only Log out. A tenant read from the server again (a reload after
 * reactivation, a new sign-in) lifts it.
 *
 * Otherwise entirely inert unless VITE_ENABLE_BILLING === 'true' (old:
 * VUE_APP_ENABLE_BILLING) — the gate skips the debt seed, the debt-warning
 * check, the tenant-watcher body, and listener registration exactly like the
 * old created()/watch early returns.
 *
 * Shows:
 * - On boot and on every tenant replacement: the daily debt warning, when
 *   company.amount_due ≥ 2 currency units, outside the 00–05h window, at most
 *   once per calendar day (AppStorageKey.debtWarningSeenDate via
 *   appStorage.local(); the seen-date is written the moment the warning
 *   qualifies, exactly like the old code).
 *
 * States (from tenant.status):
 * - inactive (expired / disabled) → the POS account lock's message
 *   (TenantService.inactiveMessageKey) + a tenant-logout button
 *   (TenantService.logout → tenantLogin).
 * - otherwise → debt alert (amount + settle/invoices hint when
 *   company.latest_invoice.path exists, reseller text otherwise) + close button.
 *
 * Parity notes:
 * - The old v-dialog was NOT fullscreen on xs → :fullscreen-enabled="false".
 * - Old error-red heading bar/alert/buttons → semantic `error` (G3).
 * - Old overlay #2c2c38/0.8 → UModal default overlay (visual only).
 */
-->
<template>
    <UModal
        :open="visible || accountInactive"
        :dismissible="false"
        :close="false"
        :ui="{ content: 'sm:max-w-[585px]' }"
    >
        <template #content>
            <div>
                <!-- Old: v-card-title.subscription-heading.error — red bar, white centered text -->
                <div class="flex justify-center bg-error p-3 text-screen-title text-inverted">
                    {{ t('subscription.heading') }}
                </div>

                <div v-if="accountInactive" class="p-6">
                    <UAlert color="error" icon="i-lucide-circle-x" variant="soft">
                        <template #description>
                            <span>{{ t(TenantService.inactiveMessageKey(tenant)) }}</span>
                        </template>
                    </UAlert>

                    <div class="mt-6 mb-1 flex justify-center">
                        <UButton
                            color="error"
                            size="lg"
                            leading-icon="i-lucide-log-out"
                            @click="logoutTenant()"
                        >
                            {{ t('general.log_out') }}
                        </UButton>
                    </div>
                </div>

                <div v-else class="p-6">
                    <UAlert color="error" icon="i-lucide-circle-x" variant="soft">
                        <template #description>
                            <div v-if="shouldShowDebtAmount()">
                                <span>
                                    <strong>{{ t('subscription.debt_amount', { value: debt.toFormat(), software: 'Konty' }) }}</strong>
                                    <br>
                                </span>
                                <span>{{ t('subscription.debt_settle') }} </span>
                                <span>{{ t('subscription.debt_invoices') }}</span>
                            </div>
                            <div v-else>
                                <span>{{ t('subscription.debt_settle_reseller', { software: 'Konty' }) }} </span>
                            </div>
                        </template>
                    </UAlert>

                    <div class="mt-6 mb-1 flex justify-center">
                        <UButton color="error" size="lg" @click="hide()">
                            {{ t('general.close') }}
                        </UButton>
                    </div>
                </div>
            </div>
        </template>
    </UModal>
</template>

<script setup lang="ts">
import { ref, shallowRef, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import moment from 'moment-timezone'
import { appStorage, TenantService, AppStorageKey, Money } from 'app-core'
import { useAppStore } from '@/stores/useAppStore'

const { t } = useI18n()
const router = useRouter()
const { tenant } = storeToRefs(useAppStore())

const visible = ref(false)
// shallowRef: Money instances are replaced wholesale, never mutated.
const debt = shallowRef(Money.parse(0))

/** The account lock: expired or disabled, whatever billing says (see the header). */
const accountInactive = computed(() => TenantService.isInactive(tenant.value))

// created() — old early-return: the debt seed and the debt-warning check run only
// when billing is enabled.
if (import.meta.env.VITE_ENABLE_BILLING === 'true') {
    void (async () => {
        debt.value = tenant.value && tenant.value.company
            ? Money.parse(tenant.value.company.amount_due)
            : Money.parse(0)

        if (await shouldShowDebtWarning()) {
            visible.value = true
        }
    })()
}

// watch: { async tenant() { … } } — re-runs the debt warning whenever the store
// tenant is replaced (e.g. after a /me reload).
watch(tenant, async () => {
    if (import.meta.env.VITE_ENABLE_BILLING !== 'true') return

    debt.value = tenant.value && tenant.value.company
        ? Money.parse(tenant.value.company.amount_due)
        : Money.parse(0)

    if (await shouldShowDebtWarning()) {
        visible.value = true
    }
})

function hide() {
    visible.value = false
}

async function shouldShowDebtWarning(): Promise<boolean> {
    if (debt.value.toUnit() < 2) return false

    if (moment().hour() <= 5) return false

    const today = moment().format('YYYY-MM-DD')
    const debtWarningSeenDateAt = await appStorage.local().getItem<string>(AppStorageKey.debtWarningSeenDate)

    if (!debtWarningSeenDateAt || today !== debtWarningSeenDateAt) {
        // Fire-and-forget, exactly like the old code (setItem was not awaited).
        appStorage.local().setItem(AppStorageKey.debtWarningSeenDate, today)
        return true
    }

    return false
}

function shouldShowDebtAmount(): boolean {
    return !!(tenant.value
        && tenant.value.company
        && tenant.value.company.latest_invoice
        && tenant.value.company.latest_invoice.path)
}

/**
 * Log out Tenant
 */
function logoutTenant() {
    TenantService.logout()
    router.push({ name: 'tenantLogin' }).catch(() => {})
}
</script>
